import React, { useState } from "react"
import { makeStyles, useTheme } from "@material-ui/styles"
import { Grid, List, Hidden, Drawer, Box, useMediaQuery, IconButton } from "@material-ui/core"
import { Switch, Route, useParams, Redirect, useHistory } from "react-router-dom"
import Config from "react-global-configuration"
import { FilterList as FilterListIcon } from "@material-ui/icons"
import {
  AreaHeader,
  Content,
  MainHeading,
  SubNavigation,
  DesktopCreateJobButton,
  MobileCreateJobButton,
  SubNavigationListItem,
  ModuleHeading,
  RowBox,
  DrawerHeading,
  HelpBanner,
  BackToTop,
} from "../../components"
import { useQueryTemplateCategories, useQuerySettings, useQueryOrganisationTemplateCounts } from "../../data"

import Library from "./Library"
import Organisation from "./Organisation"
import { useProcessUtils } from "../../utils"

const useStyles = makeStyles((theme) => ({
  filterButton: {
    paddingTop: ({ isInline }) => (isInline ? 6 : "inherit"),
    paddingBottom: ({ isInline }) => (isInline ? 6 : "inherit"),
  },
  subNav: {
    paddingTop: 0,
    borderRadius: "0 0 8px 8px",
    "&&:first-of-type": {
      marginBottom: 0,
      borderRadius: "8px 8px 0 0",
      paddingBottom: 0,
    },
  },
  subNavTitle: {
    paddingTop: 0,
  },
  subNavList: {
    maxWidth: 240 - theme.spacing(1),
  },
  subNavItem: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  featuredIcon: {
    marginLeft: theme.spacing(1),
  },
}))

const TemplateRoutes = () => (
  <Switch>
    <Route path="/templates/library/:category/:id/:action" component={Library} />
    <Route path="/templates/library/:category" component={Library} />
    <Route path="/templates/:set/:id/:action" component={Organisation} />
    <Route path="/templates/:set" component={Organisation} />
    <Redirect to="/templates/active-recurring" />
  </Switch>
)

const Templates = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isInline = useMediaQuery(theme.breakpoints.down("xs"))
  const { getFriendlySetName } = useProcessUtils()
  const { set, id, action, category } = useParams()
  const history = useHistory()

  const [openSubNav, setOpenSubNav] = useState(false)
  const { data: templateCategoriesData } = useQueryTemplateCategories()

  const { lang } = useQuerySettings()

  const baseUri = `/templates/${set || "active-recurring"}`

  const handleToggleSubNav = () => {
    setOpenSubNav(!openSubNav)
  }

  const handleCreate = (type) => {
    history.push(`${baseUri}/new/${type}`)
  }

  let heading
  if (category) {
    if (templateCategoriesData) {
      const templateCategory = templateCategoriesData.templateCategories.find((item) => item.id === category)
      heading = templateCategory ? templateCategory.name : "Category"
    }
  } else {
    heading = getFriendlySetName(set)
  }

  const open = id && action

  if (isInline && open) {
    return <TemplateRoutes />
  }

  return (
    <>
      <AreaHeader
        title={lang.area.templates}
        titleIcon="templates-light"
        desktopButtons={<DesktopCreateJobButton onCreateClick={handleCreate} isTemplates />}
        mobileButtons={<MobileCreateJobButton onCreateClick={handleCreate} isTemplates />}
      />

      <Content full start>
        <HelpBanner name="templates" />

        <Grid container direction="row">
          <Hidden mdDown>
            <Grid item xs={12} lg={3}>
              <TemplatesSubNav open={openSubNav} onClose={handleToggleSubNav} />
              <BackToTop from={300} />
            </Grid>
          </Hidden>
          <Hidden lgUp>
            <TemplatesSubNav open={openSubNav} onClose={handleToggleSubNav} />
          </Hidden>
          <Grid item xs={12} lg={9}>
            <RowBox flexGrow={1}>
              <Box flexGrow={1}>
                <Hidden mdDown>
                  <MainHeading mb={3.75}>{heading}</MainHeading>
                </Hidden>
                <Hidden lgUp>
                  <Box pb={1}>
                    <ModuleHeading noMargin>{heading}</ModuleHeading>
                  </Box>
                </Hidden>
              </Box>
              <Box alignSelf="center" display={{ sm: "flex", lg: "none" }}>
                <IconButton onClick={handleToggleSubNav} className={classes.filterButton}>
                  <FilterListIcon />
                </IconButton>
              </Box>
            </RowBox>

            <TemplateRoutes />
          </Grid>
        </Grid>
      </Content>
    </>
  )
}

const TemplatesSubNav = ({ open, onClose }) => {
  const classes = useStyles()
  const { data: templateCategoriesData } = useQueryTemplateCategories()
  const { data: organisationTemplateCountsData } = useQueryOrganisationTemplateCounts()
  const { set, category } = useParams()
  const { featuredCategory: featuredId } = Config.get("templates")

  const featuredCategory =
    templateCategoriesData && templateCategoriesData.templateCategories.find(({ id }) => id === featuredId)

  const counts = organisationTemplateCountsData && organisationTemplateCountsData.organisationTemplateCounts

  const handleClick = () => {
    onClose && onClose()
  }

  if (!counts) return null

  const myActiveNav = (
    <List className={classes.subNavList}>
      <SubNavigationListItem
        to="/templates/active-recurring"
        text="Recurring"
        badgeCount={counts.activeRecurring}
        badgeVariant="text"
        active={set === "active-recurring"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/active-oneoff"
        text="One-off"
        badgeCount={counts.activeOneoff}
        badgeVariant="text"
        active={set === "active-oneoff"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/active-adhoc"
        text="Ad hoc"
        badgeCount={counts.activeAdhoc}
        badgeVariant="text"
        active={set === "active-adhoc"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/active-tasks"
        text="Tasks"
        badgeCount={counts.activeTasks}
        badgeVariant="text"
        active={set === "active-tasks"}
        onClick={handleClick}
      />
    </List>
  )

  const myInactiveNav = (
    <List className={classes.subNavList}>
      <SubNavigationListItem
        to="/templates/inactive-recurring"
        text="Recurring"
        badgeCount={counts.inactiveRecurring}
        badgeVariant="text"
        active={set === "inactive-recurring"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/inactive-oneoff"
        text="One-off"
        badgeCount={counts.inactiveOneoff}
        badgeVariant="text"
        active={set === "inactive-oneoff"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/inactive-adhoc"
        text="Ad hoc"
        badgeCount={counts.inactiveAdhoc}
        badgeVariant="text"
        active={set === "inactive-adhoc"}
        onClick={handleClick}
      />
      <SubNavigationListItem
        to="/templates/inactive-tasks"
        text="Tasks"
        badgeCount={counts.inactiveTasks}
        badgeVariant="text"
        active={set === "inactive-tasks"}
        onClick={handleClick}
      />
    </List>
  )

  const libraryNav = templateCategoriesData && (
    <List className={classes.subNavList}>
      {featuredCategory && (
        <SubNavigationListItem
          to={`/templates/library/${featuredCategory.id}`}
          text={featuredCategory.name}
          rightIcon={{ name: "featured-star", className: classes.featuredIcon }}
          badgeCount={featuredCategory.items.count}
          badgeVariant="text"
          active={category === featuredCategory.id}
          onClick={handleClick}
        />
      )}
      {templateCategoriesData.templateCategories
        .filter((item) => item.id !== featuredCategory.id && item.items.count)
        .map(({ id, name, items: { count } }) => (
          <SubNavigationListItem
            key={id}
            to={`/templates/library/${id}`}
            text={name}
            badgeCount={count}
            badgeVariant="text"
            active={category === id}
            onClick={handleClick}
          />
        ))}
    </List>
  )

  return (
    <>
      <Hidden lgUp>
        <Drawer open={open} onClose={onClose}>
          <Box mt={2}>
            <DrawerHeading>My active jobs</DrawerHeading>
            {myActiveNav}
            <DrawerHeading>My inactive jobs</DrawerHeading>
            {myInactiveNav}
            <DrawerHeading>Template library</DrawerHeading>
            {libraryNav}
          </Box>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <SubNavigation title="My active jobs" className={classes.subNav}>
          {myActiveNav}
        </SubNavigation>
        <SubNavigation title="My inactive jobs" className={classes.subNav} titleClassName={classes.subNavTitle}>
          {myInactiveNav}
        </SubNavigation>
        {libraryNav && <SubNavigation title="Template library">{libraryNav}</SubNavigation>}
      </Hidden>
    </>
  )
}

export default Templates
